import { useCallback, useState } from 'react';

/**
 * Calls Pro Studio's own `/ai/parse-sop` route — see
 * `src/server-only/ai/router.ts`. Same-origin, so this is a plain `fetch`
 * with no separate base URL, matching how `use-test-api.ts` reaches
 * `/test/*`.
 */

export interface ParsedSop {
  brandName: string;
  tone: string;
  knowledgeAgentContext: string;
}

export type ParseSopState = 'idle' | 'loading' | 'error';

export const useParseSop = () => {
  const [state, setState] = useState<ParseSopState>('idle');
  const [error, setError] = useState<string | null>(null);

  const parseSop = useCallback(
    async (text: string): Promise<ParsedSop | null> => {
      setState('loading');
      setError(null);

      try {
        const response = await fetch('/ai/parse-sop', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ text }),
        });

        const body = await response.json().catch(() => null);

        if (!response.ok) {
          const detail =
            (body as { detail?: string; error?: string } | null)?.detail ??
            (body as { error?: string } | null)?.error ??
            response.statusText;
          throw new Error(detail);
        }

        setState('idle');
        return body as ParsedSop;
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Could not parse the SOP.';
        setError(message);
        setState('error');
        return null;
      }
    },
    []
  );

  return { parseSop, state, error };
};
